import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export function PaymentSuccessPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setInfo(data); setState("paid"); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setState("expired"); return; }
      } catch { /* retry */ }
      if (tries.current < 10) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  const isAdhesion = info?.kind === "adhesion";

  return (
    <main className="pt-40 pb-24 px-6 md:px-12 max-w-3xl mx-auto text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Clock size={40} strokeWidth={1.2} className="mx-auto text-cinabre animate-pulse" />
          <h1 className="mt-8 font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">
            {en ? <>Verifying your <em className="italic">payment…</em></> : <>Vérification du <em className="italic">paiement…</em></>}
          </h1>
          <p className="mt-6 text-fg/70 leading-relaxed">{en ? "One moment — we are confirming your transaction with Stripe." : "Un instant — nous confirmons votre transaction auprès de Stripe."}</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-cinabre" />
          <div className="meta-label text-cinabre mt-8">§ {info?.kind === "boutique" ? (en ? "Order confirmed" : "Commande confirmée") : isAdhesion ? (en ? "Membership confirmed" : "Adhésion confirmée") : (en ? "Donation confirmed" : "Don confirmé")}</div>
          <h1 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight" data-testid="payment-confirmed">
            {en ? <>Thank you, from the bottom <em className="italic">of the canal.</em></> : <>Merci, du fond <em className="italic">du canal.</em></>}
          </h1>
          {info?.kind === "boutique" ? (
            <>
              <p className="mt-6 text-fg/75 leading-relaxed max-w-xl mx-auto">
                {en ? <>Your order "{info.product_name}"{info.size ? ` (size ${info.size})` : ""} is confirmed — a summary email has been sent. It will be shipped to the address provided.</> : <>Votre commande « {info.product_name} »{info.size ? ` (taille ${info.size})` : ""} est confirmée — un email récapitulatif vous a été envoyé. Elle sera expédiée à l'adresse indiquée.</>}
              </p>
              {info.edition_number && (
                <div className="mt-8 inline-block border border-cinabre px-8 py-5" data-testid="edition-number">
                  <div className="meta-label text-cinabre">§ {en ? "Your copy" : "Votre exemplaire"}</div>
                  <div className="mt-2 font-serif text-5xl md:text-6xl">n° {info.edition_number}<span className="text-fg/50">/30</span></div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">{en ? "Numbered & signed on the back" : "Numéroté & signé au dos"}</div>
                </div>
              )}
            </>
          ) : (
            <p className="mt-6 text-fg/75 leading-relaxed max-w-xl mx-auto">
              {isAdhesion
                ? (en ? "Welcome to the Arteurs family. Your personalised membership card will be sent within 15 days — and a confirmation email is on its way." : "Bienvenue dans la famille des Arteurs. Votre carte de membre nominative vous sera envoyée sous 15 jours — et un email de confirmation arrive dans votre boîte.")
                : (en ? "Your donation extends an artwork, a path, an artist by the water. A confirmation email has been sent; your tax receipt will follow." : "Votre don prolonge une œuvre, un chemin, un artiste au bord de l'eau. Un email de confirmation vous a été envoyé, votre reçu fiscal suivra.")}
            </p>
          )}
          {info?.amount != null && info?.kind !== "boutique" && (
            <div className="mt-8 font-serif text-5xl text-cinabre">{(info.amount / 100).toFixed(0)} €</div>
          )}
          {!isAdhesion && info?.kind !== "boutique" && (
            <Link to="/donateurs" className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre" data-testid="success-donors-wall">
              {en ? "See the donors' wall →" : "Voir le mur des donateurs →"}
            </Link>
          )}
        </>
      )}
      {(state === "expired" || state === "error") && (
        <>
          <XCircle size={40} strokeWidth={1.2} className="mx-auto text-fg/50" />
          <h1 className="mt-8 font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">
            {en ? <>Payment not <em className="italic">confirmed.</em></> : <>Paiement non <em className="italic">confirmé.</em></>}
          </h1>
          <p className="mt-6 text-fg/70 leading-relaxed">
            {state === "expired"
              ? (en ? "The payment session expired or failed. No amount was charged." : "La session de paiement a expiré ou échoué. Aucun montant n'a été débité.")
              : (en ? "We could not confirm the payment. If you were charged, contact us — we will check." : "Nous n'avons pas pu confirmer le paiement. Si vous avez été débité, contactez-nous — nous vérifierons.")}
          </p>
        </>
      )}
      <Link to="/rejoindre" className="btn-editorial mt-12 inline-flex" data-testid="payment-back">
        <ArrowLeft size={14} /> {en ? 'Back to "Support Us"' : "Retour à « Je participe »"}
      </Link>
    </main>
  );
}

export function PaymentCancelPage() {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <main className="pt-40 pb-24 px-6 md:px-12 max-w-3xl mx-auto text-center" data-testid="payment-cancel-page">
      <div className="meta-label text-cinabre">§ {en ? "Payment cancelled" : "Paiement annulé"}</div>
      <h1 className="mt-6 font-serif font-light text-4xl md:text-6xl leading-tight tracking-tight">
        {en ? <>The thread stays <em className="italic">open.</em></> : <>Le fil reste <em className="italic">ouvert.</em></>}
      </h1>
      <p className="mt-6 text-fg/70 leading-relaxed max-w-xl mx-auto">
        {en ? "Your payment was cancelled — no amount was charged. You can try again whenever you like; the canal is not going anywhere." : "Votre paiement a été annulé — aucun montant n'a été débité. Vous pouvez recommencer quand vous voulez, le canal ne bouge pas."}
      </p>
      <Link to="/rejoindre" className="btn-editorial mt-12 inline-flex" data-testid="payment-cancel-back">
        <ArrowLeft size={14} /> {en ? 'Back to "Support Us"' : "Retour à « Je participe »"}
      </Link>
    </main>
  );
}
